import React, { useState, useEffect, useCallback, useMemo } from "react";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Users, Plus, Phone, Clock, Calendar, DollarSign,
  AlertTriangle, Edit, Trash2, CalendarDays, UserPlus, Send, Lock, FileText, Merge, Loader2,
  Search, X,
} from "lucide-react";
import GroupModal from "@/components/groups/GroupModal";
import ScheduleModal from "@/components/groups/ScheduleModal";
import PinDialog from "@/components/PinDialog";
import MemberModal from "@/components/groups/MemberModal";
import ExportButtons from "@/components/ExportButtons";

const STATUS_BADGE = {
  active: { label: "Còn hạn", cls: "bg-lime-500/20 text-lime-400 border-lime-500/30" },
  expiring_soon: { label: "Sắp hết hạn", cls: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  expired: { label: "Quá hạn", cls: "bg-red-500/20 text-red-400 border-red-500/30" },
};

function formatDateVN(d) {
  if (!d) return "\u2014";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("vi-VN");
  } catch { return d; }
}

function getScheduleTimeDisplay(schedule) {
  if (!schedule?.weekly_schedule) return null;
  const enabled = schedule.weekly_schedule.filter(w => w.enabled);
  if (enabled.length === 0) return null;
  const times = enabled.map(w => `${w.start_time}-${w.end_time}`);
  const uniqueTimes = [...new Set(times)];
  return uniqueTimes.join(", ");
}

function getScheduleDaysDisplay(schedule) {
  if (!schedule?.weekly_schedule) return null;
  const dayNames = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];
  return schedule.weekly_schedule.filter(w => w.enabled).map(w => dayNames[w.day_of_week]).join(", ");
}

function getRemainingSessionsDisplay(schedule) {
  if (!schedule?.end_date) return null;
  const today = new Date();
  const end = new Date(schedule.end_date + "T23:59:59");
  if (end <= today) return "Đã hết lịch";
  const enabled = (schedule.weekly_schedule || []).filter(w => w.enabled);
  if (enabled.length === 0) return null;
  const enabledDows = new Set(enabled.map(w => w.day_of_week));
  let count = 0;
  const current = new Date(today);
  current.setHours(0, 0, 0, 0);
  while (current <= end) {
    const dow = current.getDay();
    if (enabledDows.has(dow)) count++;
    current.setDate(current.getDate() + 1);
  }
  return `${count} buổi còn lại`;
}

export default function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [alerts, setAlerts] = useState({ expired_members: [], expiring_soon_members: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("free");

  // Modals
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleGroup, setScheduleGroup] = useState(null);
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [memberGroup, setMemberGroup] = useState(null);
  const [editingMember, setEditingMember] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [gRes, aRes] = await Promise.all([
        api.get("/groups"),
        api.get("/groups/alerts"),
      ]);
      setGroups(gRes.data.groups || []);
      setAlerts(aRes.data || { expired_members: [], expiring_soon_members: [] });
    } catch (err) {
      console.error("Failed to load groups", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = groups.filter(g => g.group_type === activeTab);
  const freeCnt = groups.filter(g => g.group_type === "free").length;
  const managedCnt = groups.filter(g => g.group_type === "managed").length;

  const handleCreateGroup = () => { setEditingGroup(null); setGroupModalOpen(true); };
  const handleEditGroup = (g) => { setEditingGroup(g); setGroupModalOpen(true); };
  const handleSchedule = (g) => { setScheduleGroup(g); setScheduleModalOpen(true); };
  const handleAddMember = (g) => { setMemberGroup(g); setEditingMember(null); setMemberModalOpen(true); };
  const handleEditMember = (g, m) => { setMemberGroup(g); setEditingMember(m); setMemberModalOpen(true); };

  // PIN-protected deletes
  const [pinOpen, setPinOpen] = useState(false);
  const [pinAction, setPinAction] = useState(null);
  const [pinDesc, setPinDesc] = useState("");

  const handleDeleteGroup = (g) => {
    setPinDesc(`Xóa hội "${g.name}"? Tất cả thành viên sẽ bị xóa.`);
    setPinAction(() => async () => {
      try {
        await api.delete(`/groups/${g.id}`);
        toast.success("Đã xóa hội");
        loadData();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Lỗi khi xóa");
      }
    });
    setPinOpen(true);
  };

  const handleDeleteMember = (groupId, memberId, memberName) => {
    setPinDesc(`Xóa thành viên "${memberName}"?`);
    setPinAction(() => async () => {
      try {
        await api.delete(`/groups/${groupId}/members/${memberId}`);
        toast.success("Đã xóa thành viên");
        loadData();
      } catch (err) {
        toast.error("Lỗi khi xóa thành viên");
      }
    });
    setPinOpen(true);
  };

  const handleExtendMember = async (groupId, memberId, months) => {
    try {
      await api.post(`/groups/${groupId}/members/${memberId}/extend`, { months });
      toast.success(`Đã gia hạn thêm ${months} tháng`);
      loadData();
    } catch (err) {
      toast.error("Lỗi khi gia hạn");
    }
  };

  // Merge members
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeGroup, setMergeGroup] = useState(null);
  const [mergeMember, setMergeMember] = useState(null);

  const handleMerge = (group, member) => {
    setMergeGroup(group);
    setMergeMember(member);
    setMergeOpen(true);
  };

  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinTarget, setPinTarget] = useState(null);
  const [pinValue, setPinValue] = useState("");
  const [sendingZalo, setSendingZalo] = useState({});

  const handleSendZalo = async (groupId, memberId, memberName, forcePin) => {
    const key = `${groupId}-${memberId}`;
    setSendingZalo(p => ({ ...p, [key]: true }));
    try {
      const body = forcePin ? { pin: forcePin } : {};
      const { data } = await api.post(`/groups/${groupId}/members/${memberId}/send-zalo`, body);
      toast.success(data.message || "Đã gửi");
      loadData();
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        setPinTarget({ groupId, memberId, memberName, detail: err.response?.data?.detail });
        setPinValue("");
        setPinDialogOpen(true);
      } else {
        toast.error(err.response?.data?.detail || "Lỗi gửi thông báo");
      }
    } finally {
      setSendingZalo(p => ({ ...p, [key]: false }));
    }
  };

  const handlePinConfirm = () => {
    if (!pinTarget) return;
    setPinDialogOpen(false);
    handleSendZalo(pinTarget.groupId, pinTarget.memberId, pinTarget.memberName, pinValue);
  };

  const getZaloCooldown = (lastSent) => {
    if (!lastSent) return null;
    try {
      const diff = (Date.now() - new Date(lastSent).getTime()) / 3600000;
      if (diff < 72) return Math.ceil(72 - diff);
      return null;
    } catch { return null; }
  };

  const hasAlerts = alerts.expired_members.length > 0 || alerts.expiring_soon_members.length > 0;

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="groups-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#F8FAFC] tracking-tight">Quản lý Hội</h1>
          <p className="text-slate-400 text-sm mt-1">Quản lý nhóm khách hàng thường xuyên</p>
        </div>
        <div className="flex items-center gap-3">
          <ExportButtons endpoint="/export/groups" label="hoi" />
          <Button onClick={handleCreateGroup} className="bg-lime-500 hover:bg-lime-600 text-[#0f172a] font-semibold gap-1.5" data-testid="create-group-btn">
            <Plus className="w-4 h-4" /> Tạo hội
          </Button>
        </div>
      </div>

      {/* Alert Banner */}
      {hasAlerts && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-3 space-y-1.5" data-testid="alerts-banner">
          {alerts.expired_members.map((m, i) => (
            <div key={`exp-${i}`} className="flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
              <span className="text-red-400 font-medium">{m.name}</span>
              <span className="text-slate-500">({m.group_name})</span>
              <span className="text-red-400">— Quá hạn {m.days_overdue} ngày</span>
              {m.group_id && m.id && (
                <button
                  onClick={() => handleSendZalo(m.group_id, m.id, m.name)}
                  disabled={sendingZalo[`${m.group_id}-${m.id}`]}
                  className="ml-auto text-xs px-2 py-0.5 rounded bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 flex items-center gap-1 shrink-0"
                  data-testid={`alert-zalo-${m.id}`}
                >
                  <Send className="w-3 h-3" /> Gửi Zalo
                </button>
              )}
            </div>
          ))}
          {alerts.expiring_soon_members.map((m, i) => (
            <div key={`soon-${i}`} className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-amber-400 shrink-0" />
              <span className="text-amber-400 font-medium">{m.name}</span>
              <span className="text-slate-500">({m.group_name})</span>
              <span className="text-amber-400">— Sắp hết hạn (còn {m.days_left} ngày)</span>
              {m.group_id && m.id && (
                <button
                  onClick={() => handleSendZalo(m.group_id, m.id, m.name)}
                  disabled={sendingZalo[`${m.group_id}-${m.id}`]}
                  className="ml-auto text-xs px-2 py-0.5 rounded bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 flex items-center gap-1 shrink-0"
                  data-testid={`alert-zalo-${m.id}`}
                >
                  <Send className="w-3 h-3" /> Gửi Zalo
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-[#0f172a] border border-[#334155] rounded-lg p-1" data-testid="group-tabs">
        <button
          onClick={() => setActiveTab("free")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "free" ? "bg-[#1e293b] text-lime-400" : "text-slate-400 hover:text-slate-200"}`}
          data-testid="tab-free"
        >
          Hội tự do ({freeCnt})
        </button>
        <button
          onClick={() => setActiveTab("managed")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "managed" ? "bg-[#1e293b] text-lime-400" : "text-slate-400 hover:text-slate-200"}`}
          data-testid="tab-managed"
        >
          Hội quản lý ({managedCnt})
        </button>
        <button
          onClick={() => setActiveTab("schedules")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "schedules" ? "bg-[#1e293b] text-teal-400" : "text-slate-400 hover:text-slate-200"}`}
          data-testid="tab-schedules"
        >
          <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> Lịch hội</span>
        </button>
      </div>

      {/* Schedules Management Tab */}
      {activeTab === "schedules" && (
        <GroupSchedulesPanel groups={groups} onReload={loadData} />
      )}

      {/* Group Cards Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-[#1e293b] border border-[#334155] rounded-lg h-64 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <Users className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p>Chưa có hội {activeTab === "free" ? "tự do" : "quản lý"} nào</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(g => (
            <GroupCard
              key={g.id}
              group={g}
              onEdit={() => handleEditGroup(g)}
              onDelete={() => handleDeleteGroup(g)}
              onSchedule={() => handleSchedule(g)}
              onAddMember={() => handleAddMember(g)}
              onEditMember={(m) => handleEditMember(g, m)}
              onDeleteMember={(m) => handleDeleteMember(g.id, m.id, m.name)}
              onExtendMember={(m, months) => handleExtendMember(g.id, m.id, months)}
              onMergeMember={(m) => handleMerge(g, m)}
              onSendZalo={(m) => handleSendZalo(g.id, m.id, m.name)}
              sendingZalo={sendingZalo}
              getZaloCooldown={getZaloCooldown}
            />
          ))}
        </div>
      )}

      <GroupModal open={groupModalOpen} onOpenChange={setGroupModalOpen} group={editingGroup} onSaved={loadData} />
      <ScheduleModal open={scheduleModalOpen} onOpenChange={setScheduleModalOpen} group={scheduleGroup} onSaved={loadData} />
      <MemberModal open={memberModalOpen} onOpenChange={setMemberModalOpen} group={memberGroup} member={editingMember} onSaved={loadData} />

      <PinDialog open={pinOpen} onOpenChange={setPinOpen} onConfirm={() => { if (pinAction) pinAction(); }} title="Xác nhận xóa" description={pinDesc} />

      <MergeMemberDialog open={mergeOpen} onOpenChange={setMergeOpen} group={mergeGroup} sourceMember={mergeMember} onSaved={loadData} />

      {/* PIN Dialog */}
      <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-400" /> Nhập mã PIN
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Gửi lại trong thời gian chờ cho {pinTarget?.memberName}
            </DialogDescription>
          </DialogHeader>
          {pinTarget?.detail && <p className="text-xs text-amber-400">{pinTarget.detail}</p>}
          <Input value={pinValue} onChange={(e) => setPinValue(e.target.value)} placeholder="Nhập mã PIN"
            type="password" className="bg-[#0f172a] border-[#334155] text-slate-50" data-testid="pin-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPinDialogOpen(false)} className="border-[#334155] text-slate-300 hover:bg-[#334155]">Huỷ</Button>
            <Button onClick={handlePinConfirm} disabled={!pinValue} className="bg-lime-500 text-[#0f172a] font-semibold hover:bg-lime-400" data-testid="pin-confirm-btn">Xác nhận</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GroupCard({ group: g, onEdit, onDelete, onSchedule, onAddMember, onEditMember, onDeleteMember, onExtendMember, onMergeMember, onSendZalo, sendingZalo, getZaloCooldown }) {
  const scheduleTime = getScheduleTimeDisplay(g.schedule);
  const scheduleDays = getScheduleDaysDisplay(g.schedule);
  const remaining = getRemainingSessionsDisplay(g.schedule);
  const members = g.members || [];
  // Sort newest first
  const sortedMembers = [...members].sort((a, b) => {
    const da = a.created_at ? new Date(a.created_at) : 0;
    const db = b.created_at ? new Date(b.created_at) : 0;
    return db - da;
  });

  // Search thành viên: theo Tên + SĐT + Email
  const [memberSearch, setMemberSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const normalized = (s) => (s || "").toString().toLowerCase().trim();
  const q = normalized(memberSearch);
  const filteredMembers = q
    ? sortedMembers.filter((m) =>
        normalized(m.name).includes(q) ||
        normalized(m.phone).includes(q) ||
        normalized(m.email).includes(q)
      )
    : sortedMembers;

  return (
    <div className="bg-[#1e293b] border border-[#334155] rounded-lg overflow-hidden flex flex-col" data-testid={`group-card-${g.id}`}>
      {/* Header */}
      <div className="p-4 border-b border-[#334155]/60">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-[#F8FAFC] text-base truncate">{g.name}</h3>
            {g.group_type === "free" && g.organizer_name && (
              <p className="text-xs text-slate-400 mt-0.5">Người tổ chức: {g.organizer_name}</p>
            )}
            {g.organizer_phone && (
              <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                <Phone className="w-3 h-3" /> {g.organizer_phone}
              </div>
            )}
            {scheduleTime && (
              <div className="flex items-center gap-1 text-xs text-teal-400 mt-1">
                <Clock className="w-3 h-3" /> {scheduleDays} | {scheduleTime}
              </div>
            )}
          </div>
          <div className="flex gap-1 shrink-0 ml-2">
            <button onClick={onSchedule} className="p-1.5 rounded transition-colors" style={{ background: "rgba(59,130,246,0.15)" }}
              onMouseEnter={(e) => e.currentTarget.style.background = "rgba(59,130,246,0.25)"}
              onMouseLeave={(e) => e.currentTarget.style.background = "rgba(59,130,246,0.15)"}
              title="Lịch biểu" data-testid={`schedule-btn-${g.id}`}>
              <CalendarDays className="w-4 h-4" style={{ color: "#60a5fa" }} />
            </button>
            <button onClick={onEdit} className="p-1.5 rounded hover:bg-[#334155] text-slate-400 hover:text-lime-400 transition-colors" title="Sửa" data-testid={`edit-group-${g.id}`}>
              <Edit className="w-4 h-4" />
            </button>
            <button onClick={onDelete} className="p-1.5 rounded hover:bg-[#334155] text-slate-400 hover:text-red-400 transition-colors" title="Xóa" data-testid={`delete-group-${g.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-2 px-4 py-2 border-b border-[#334155]/40 flex-wrap">
        <Badge className="bg-slate-500/20 text-slate-300 border-slate-500/30 text-[10px] px-2 py-0.5 border">
          Tổng: {g.total_members}
        </Badge>
        <Badge className="bg-lime-500/20 text-lime-400 border-lime-500/30 text-[10px] px-2 py-0.5 border">
          HĐ: {g.active_members}
        </Badge>
        {g.group_type === "managed" && g.monthly_revenue > 0 && (
          <Badge className="bg-sky-500/20 text-sky-400 border-sky-500/30 text-[10px] px-2 py-0.5 border">
            DT: {formatVND(g.monthly_revenue)}đ
          </Badge>
        )}
        {remaining && (
          <Badge className="bg-teal-500/20 text-teal-400 border-teal-500/30 text-[10px] px-2 py-0.5 border">
            {remaining}
          </Badge>
        )}
      </div>

      {/* Members */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-between px-4 py-2 gap-2">
          <span className="text-xs text-slate-400 font-medium shrink-0">
            Thành viên ({q ? `${filteredMembers.length}/${members.length}` : members.length})
          </span>
          <div className="flex items-center gap-1.5 shrink-0">
            {searchOpen ? (
              <div className="flex items-center gap-1">
                <Input
                  autoFocus
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder="Tên, SĐT, email..."
                  className="h-7 w-40 text-xs bg-[#0f172a] border-[#334155] text-slate-200 placeholder:text-slate-600"
                  data-testid={`member-search-input-${g.id}`}
                />
                <button
                  onClick={() => { setSearchOpen(false); setMemberSearch(""); }}
                  className="p-1 rounded text-slate-500 hover:text-red-400 hover:bg-[#334155] transition-colors"
                  title="Đóng tìm kiếm"
                  data-testid={`member-search-close-${g.id}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                className="flex items-center text-slate-400 hover:text-sky-400 transition-colors p-1 rounded hover:bg-[#334155]"
                title="Tìm kiếm thành viên"
                data-testid={`member-search-btn-${g.id}`}
              >
                <Search className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={onAddMember} className="flex items-center gap-1 text-xs text-lime-400 hover:text-lime-300 transition-colors" data-testid={`add-member-${g.id}`}>
              <UserPlus className="w-3 h-3" /> Thêm
            </button>
          </div>
        </div>
        <div className="px-3 pb-3 overflow-y-auto max-h-[200px] space-y-1">
          {filteredMembers.length === 0 ? (
            <p className="text-xs text-slate-600 text-center py-4">
              {q ? `Không tìm thấy "${memberSearch}"` : "Chưa có thành viên"}
            </p>
          ) : (
            filteredMembers.map((m, index) => {
              const sb = STATUS_BADGE[m.status] || STATUS_BADGE.active;
              const memberNum = members.length - index;
              const cooldownH = getZaloCooldown ? getZaloCooldown(m.last_notification_sent_at) : null;
              const zaloKey = `${g.id}-${m.id}`;
              const isSending = sendingZalo?.[zaloKey];
              const showZaloBtn = m.status === "expiring_soon" || m.status === "expired";
              return (
                <div key={m.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-[#0f172a]/50 hover:bg-[#0f172a]/80 transition-colors group" data-testid={`member-${m.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-600 font-mono">#{memberNum}</span>
                      <span className="text-xs text-slate-200 font-medium truncate">{m.name}</span>
                      <Badge className={`${sb.cls} text-[9px] px-1 py-0 border`}>{sb.label}</Badge>
                      {m.payment_status === "paid" && (
                        <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20 text-[9px] px-1 py-0 border">Đã TT</Badge>
                      )}
                      {m.payment_status === "overdue" && (
                        <Badge className="bg-red-500/15 text-red-400 border-red-500/20 text-[9px] px-1 py-0 border">Quá hạn TT</Badge>
                      )}
                      {m.receipt_status && m.receipt_status !== "none" && (
                        <FileText className="w-3 h-3 text-sky-400 shrink-0" title="Có biên nhận" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-slate-500">
                      {m.phone && <span>{m.phone}</span>}
                      {m.order_code && <span className="font-mono text-amber-400">{m.order_code}</span>}
                      <span>{formatDateVN(m.join_date)} → {formatDateVN(m.end_date)}</span>
                    </div>
                  </div>
                  <div className="flex gap-0.5 items-center shrink-0">
                    {showZaloBtn && (
                      cooldownH ? (
                        <span className="text-[9px] text-emerald-400 px-1.5 py-0.5 flex items-center gap-0.5" title={`Còn ${cooldownH}h`}>
                          <span>✅</span> {cooldownH}h
                        </span>
                      ) : (
                        <button onClick={() => onSendZalo(m)} disabled={isSending}
                          className="text-[9px] px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 flex items-center gap-0.5"
                          data-testid={`zalo-btn-${m.id}`}>
                          <Send className="w-2.5 h-2.5" /> Zalo
                        </button>
                      )
                    )}
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      {m.status !== "active" && (
                        <button onClick={() => onExtendMember(m, 1)} className="text-[9px] px-1.5 py-0.5 rounded bg-lime-500/20 text-lime-400 hover:bg-lime-500/30" title="Gia hạn +1 tháng">
                          +1T
                        </button>
                      )}
                      <button onClick={() => onEditMember(m)} className="p-1 rounded text-slate-500 hover:text-lime-400">
                        <Edit className="w-3 h-3" />
                      </button>
                      <button onClick={() => onMergeMember(m)} className="p-1 rounded text-slate-500 hover:text-sky-400" title="Gộp thành viên">
                        <Merge className="w-3 h-3" />
                      </button>
                      <button onClick={() => onDeleteMember(m)} className="p-1 rounded text-slate-500 hover:text-red-400">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Notes */}
      {g.notes && (
        <div className="px-4 py-2 border-t border-[#334155]/40">
          <p className="text-[11px] text-slate-500 italic truncate">{g.notes}</p>
        </div>
      )}
    </div>
  );
}


function MergeMemberDialog({ open, onOpenChange, group, sourceMember, onSaved }) {
  const [searchQ, setSearchQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [keepName, setKeepName] = useState("");
  const [keepPhone, setKeepPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const debounceRef = React.useRef(null);

  useEffect(() => {
    if (open) {
      setSearchQ("");
      setResults([]);
      setSelectedCustomer(null);
      setKeepName(sourceMember?.name || "");
      setKeepPhone(sourceMember?.phone || "");
    }
  }, [open, sourceMember]);

  // Debounced autocomplete search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (searchQ.length < 1) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/customers?q=${encodeURIComponent(searchQ)}`);
        // Exclude the member's own linked customer
        const ownCid = sourceMember?.customer_id;
        setResults((data.customers || []).filter(c => c.id !== ownCid));
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQ, open, sourceMember]);

  const selectCustomer = (c) => {
    setSelectedCustomer(c);
    setSearchQ("");
    setResults([]);
    // Default: keep the customer's name/phone (since it's the "real" customer)
    setKeepName(c.name);
    setKeepPhone(c.phone || "");
  };

  // Build name/phone options
  const nameOptions = [];
  const phoneOptions = [];
  if (sourceMember) {
    nameOptions.push(sourceMember.name);
    if (sourceMember.phone) phoneOptions.push(sourceMember.phone);
  }
  if (selectedCustomer) {
    if (!nameOptions.includes(selectedCustomer.name)) nameOptions.push(selectedCustomer.name);
    if (selectedCustomer.phone && !phoneOptions.includes(selectedCustomer.phone)) phoneOptions.push(selectedCustomer.phone);
  }

  const handleSubmit = async () => {
    if (!selectedCustomer || !keepName) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/groups/${group.id}/members/merge`, {
        member_id: sourceMember.id,
        target_customer_id: selectedCustomer.id,
        keep_name: keepName,
        keep_phone: keepPhone,
      });
      toast.success(data.message || "Đã gộp thành viên");
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi gộp thành viên");
    } finally {
      setSaving(false);
    }
  };

  if (!sourceMember) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="merge-member-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2">
            <Merge className="w-5 h-5 text-sky-400" />
            Gộp thành viên với khách hàng
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Liên kết "{sourceMember.name}" trong hội với khách hàng có sẵn
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Source member info */}
          <div className="bg-[#0f172a] border border-sky-500/30 rounded-lg p-3">
            <p className="text-[10px] text-sky-400 uppercase tracking-wider font-medium mb-1">Thành viên trong hội</p>
            <p className="text-sm text-white font-semibold">{sourceMember.name}</p>
            <div className="flex gap-3 text-xs text-slate-500 mt-0.5">
              {sourceMember.phone && <span>{sourceMember.phone}</span>}
              <span>Hội: {group?.name}</span>
            </div>
          </div>

          {/* Autocomplete search customers */}
          {!selectedCustomer ? (
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Tìm khách hàng để gộp</label>
              <div className="relative">
                <Input
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="Nhập tên hoặc SĐT khách hàng..."
                  className="bg-[#0f172a] border-[#334155] text-slate-200 pr-8"
                  autoFocus
                  data-testid="merge-search-input"
                />
                {searching && <Loader2 className="absolute right-2.5 top-2.5 w-4 h-4 animate-spin text-slate-500" />}
              </div>
              {/* Results dropdown */}
              {results.length > 0 && (
                <div className="mt-1 bg-[#0f172a] border border-[#334155] rounded-lg max-h-[200px] overflow-y-auto" data-testid="merge-search-results">
                  {results.map(c => (
                    <button key={c.id} onClick={() => selectCustomer(c)}
                      className="w-full text-left px-3 py-2.5 hover:bg-[#334155]/50 border-b border-[#334155]/40 last:border-0 transition-colors"
                      data-testid={`merge-result-${c.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-200 font-medium">{c.name}</span>
                        {c.tags?.length > 0 && (
                          <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20 text-[9px] px-1 py-0 border">{c.tags[0]}</Badge>
                        )}
                      </div>
                      <div className="flex gap-3 text-[11px] text-slate-500 mt-0.5">
                        {c.phone && <span>{c.phone}</span>}
                        {c.email && !c.email.includes("@karinox.local") && <span>{c.email}</span>}
                        {c.source && <span>{c.source}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {searchQ.length >= 1 && !searching && results.length === 0 && (
                <p className="text-xs text-slate-600 mt-2 text-center">Không tìm thấy khách hàng "{searchQ}"</p>
              )}
            </div>
          ) : (
            <>
              {/* Selected customer */}
              <div className="bg-[#0f172a] border border-lime-500/30 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-lime-400 uppercase tracking-wider font-medium">Khách hàng gộp vào</p>
                  <button onClick={() => setSelectedCustomer(null)} className="text-[10px] text-slate-500 hover:text-slate-300 underline">Đổi</button>
                </div>
                <p className="text-sm text-white font-semibold mt-1">{selectedCustomer.name}</p>
                <div className="flex gap-3 text-xs text-slate-500 mt-0.5">
                  {selectedCustomer.phone && <span>{selectedCustomer.phone}</span>}
                  {selectedCustomer.email && !selectedCustomer.email.includes("@karinox.local") && <span>{selectedCustomer.email}</span>}
                </div>
              </div>

              {/* Keep name */}
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Giữ lại tên</label>
                <div className="flex flex-col gap-1">
                  {nameOptions.map(n => (
                    <button key={n} onClick={() => setKeepName(n)}
                      className={`text-left px-3 py-2 rounded-md text-sm border transition-colors ${keepName === n ? "bg-sky-500/15 text-sky-300 border-sky-500/40" : "text-slate-400 border-[#334155] hover:border-slate-400"}`}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              {/* Keep phone */}
              {phoneOptions.length > 0 && (
                <div>
                  <label className="text-xs text-slate-400 mb-1.5 block">Giữ lại SĐT</label>
                  <div className="flex flex-col gap-1">
                    {phoneOptions.map(p => (
                      <button key={p} onClick={() => setKeepPhone(p)}
                        className={`text-left px-3 py-2 rounded-md text-sm border transition-colors font-mono ${keepPhone === p ? "bg-sky-500/15 text-sky-300 border-sky-500/40" : "text-slate-400 border-[#334155] hover:border-slate-400"}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 space-y-1.5">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Kết quả sau gộp</p>
                <p className="text-sm text-white font-semibold">{keepName} {keepPhone && <span className="text-slate-400 font-mono font-normal text-xs ml-1">({keepPhone})</span>}</p>
                <p className="text-[10px] text-slate-500">Thành viên hội sẽ được liên kết với khách hàng "{selectedCustomer.name}"</p>
                {sourceMember.customer_id && sourceMember.customer_id !== selectedCustomer.id && (
                  <p className="text-[10px] text-amber-400">Tài khoản khách hàng cũ (tự tạo) sẽ bị xóa nếu không còn liên kết</p>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400">Hủy</Button>
          <Button onClick={handleSubmit} disabled={saving || !selectedCustomer || !keepName}
            className="bg-sky-500 hover:bg-sky-600 text-white font-semibold gap-1.5" data-testid="merge-confirm-btn">
            <Merge className="w-4 h-4" /> {saving ? "Đang gộp..." : "Xác nhận gộp"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// --- Group Schedules Panel ---
function GroupSchedulesPanel({ groups, onReload }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterGroup, setFilterGroup] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [sortBy, setSortBy] = useState("date_asc");
  const [showConflictsOnly, setShowConflictsOnly] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  // Deep conflict check state
  const [conflictData, setConflictData] = useState(null);
  const [conflictLoading, setConflictLoading] = useState(false);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictSelected, setConflictSelected] = useState(new Set());
  const [conflictDeleting, setConflictDeleting] = useState(false);
  const [conflictPinOpen, setConflictPinOpen] = useState(false);
  // Orphan check state
  const [orphanData, setOrphanData] = useState(null);
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [orphanDialogOpen, setOrphanDialogOpen] = useState(false);
  const [orphanSelected, setOrphanSelected] = useState(new Set());
  const [orphanPinOpen, setOrphanPinOpen] = useState(false);
  const [orphanFilter, setOrphanFilter] = useState("");

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterGroup) params.set("group_id", filterGroup);
      if (filterFrom) params.set("date_from", filterFrom);
      if (filterTo) params.set("date_to", filterTo);
      const { data } = await api.get(`/groups/all-schedules?${params}`);
      setSchedules(data.bookings || []);
    } catch (err) {
      toast.error("Lỗi tải lịch hội");
    } finally {
      setLoading(false);
    }
  }, [filterGroup, filterFrom, filterTo]);

  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  // Detect conflicts: same court + same date + overlapping time
  const conflicts = useMemo(() => {
    const conflictIds = new Set();
    for (let i = 0; i < schedules.length; i++) {
      for (let j = i + 1; j < schedules.length; j++) {
        const a = schedules[i], b = schedules[j];
        if (a.court_name === b.court_name && a.date === b.date) {
          // Check time overlap
          if (a.start_time < b.end_time && b.start_time < a.end_time) {
            conflictIds.add(a.id);
            conflictIds.add(b.id);
          }
        }
      }
    }
    return conflictIds;
  }, [schedules]);

  // Apply filters and sort
  const displayed = useMemo(() => {
    let list = [...schedules];
    if (filterStatus) list = list.filter(s => s.status === filterStatus);
    if (showConflictsOnly) list = list.filter(s => conflicts.has(s.id));
    // Sort
    list.sort((a, b) => {
      if (sortBy === "date_asc") return a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time);
      if (sortBy === "date_desc") return b.date.localeCompare(a.date) || b.start_time.localeCompare(a.start_time);
      if (sortBy === "group") return a.group_name.localeCompare(b.group_name);
      if (sortBy === "court") return a.court_name.localeCompare(b.court_name);
      return 0;
    });
    return list;
  }, [schedules, filterStatus, showConflictsOnly, conflicts, sortBy]);

  const toggleSelect = (id) => {
    setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };
  const selectAll = () => {
    if (selected.size === displayed.length) setSelected(new Set());
    else setSelected(new Set(displayed.map(s => s.id)));
  };
  const selectConflicts = () => { setSelected(new Set(conflicts)); };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/groups/delete-schedules", { booking_ids: Array.from(selected) });
      toast.success(data.message);
      setSelected(new Set());
      fetchSchedules();
      onReload();
    } catch (err) { toast.error("Lỗi xoá: " + (err.response?.data?.detail || "")); }
    finally { setDeleting(false); setPinOpen(false); }
  };

  // --- Deep conflict check ---
  const runConflictCheck = async () => {
    setConflictLoading(true);
    try {
      const { data } = await api.post("/groups/check-conflicts");
      setConflictData(data);
      setConflictSelected(new Set());
      setConflictDialogOpen(true);
    } catch { toast.error("Lỗi kiểm tra trùng lịch"); }
    finally { setConflictLoading(false); }
  };

  const toggleConflictSelect = (id) => {
    setConflictSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };
  const selectAllConflictGroupBookings = () => {
    if (!conflictData?.conflicts) return;
    const ids = new Set();
    conflictData.conflicts.forEach(c => {
      // For group_vs_group: select both; for group_vs_regular: select only the group one
      if (c.booking_a.source === "group_auto") ids.add(c.booking_a.id);
      if (c.booking_b.source === "group_auto") ids.add(c.booking_b.id);
    });
    setConflictSelected(ids);
  };
  const deleteConflictBookings = async () => {
    if (conflictSelected.size === 0) return;
    setConflictDeleting(true);
    try {
      const { data } = await api.post("/groups/delete-schedules", { booking_ids: Array.from(conflictSelected) });
      toast.success(data.message);
      setConflictSelected(new Set());
      setConflictPinOpen(false);
      setConflictDialogOpen(false);
      setConflictData(null);
      fetchSchedules();
      onReload();
    } catch (err) { toast.error("Lỗi xoá: " + (err.response?.data?.detail || "")); }
    finally { setConflictDeleting(false); }
  };

  // --- Orphan / invalid schedule check ---
  const runOrphanCheck = async () => {
    setOrphanLoading(true);
    try {
      const { data } = await api.post("/groups/check-orphans");
      setOrphanData(data);
      setOrphanSelected(new Set());
      setOrphanFilter("");
      setOrphanDialogOpen(true);
    } catch { toast.error("Lỗi kiểm tra lịch lỗi"); }
    finally { setOrphanLoading(false); }
  };
  const toggleOrphanSelect = (id) => {
    setOrphanSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };
  const selectAllOrphans = () => {
    if (!orphanData?.issues) return;
    const filtered = orphanFilter
      ? orphanData.issues.filter(i => i.issue === orphanFilter)
      : orphanData.issues;
    setOrphanSelected(new Set(filtered.map(i => i.id)));
  };
  const deleteOrphanBookings = async () => {
    if (orphanSelected.size === 0) return;
    try {
      // Use delete-invalid-bookings endpoint (works for all booking types)
      const { data } = await api.post("/groups/delete-invalid-bookings", { booking_ids: Array.from(orphanSelected) });
      toast.success(data.message);
      setOrphanSelected(new Set());
      setOrphanPinOpen(false);
      setOrphanDialogOpen(false);
      setOrphanData(null);
      fetchSchedules();
      onReload();
    } catch (err) { toast.error("Lỗi xoá: " + (err.response?.data?.detail || "")); }
  };

  const STATUS_CLS = {
    confirmed: "bg-lime-500/20 text-lime-400",
    pending: "bg-amber-500/20 text-amber-400",
    completed: "bg-sky-500/20 text-sky-400",
    cancelled: "bg-red-500/20 text-red-400",
  };

  return (
    <div className="space-y-3" data-testid="group-schedules-panel">
      {/* Conflict Warning */}
      {conflicts.size > 0 && (
        <div className="flex items-center justify-between bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2.5" data-testid="conflict-warning">
          <div className="flex items-center gap-2 text-red-400 text-xs font-medium">
            <AlertTriangle className="w-4 h-4" />
            Phát hiện {conflicts.size} lịch bị trùng (cùng sân + cùng giờ)
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowConflictsOnly(!showConflictsOnly)}
              className={`text-xs px-2 py-1 rounded ${showConflictsOnly ? "bg-red-500/20 text-red-400" : "text-slate-400 hover:text-red-400"}`}>
              {showConflictsOnly ? "Hiện tất cả" : "Chỉ xem trùng"}
            </button>
            <button onClick={selectConflicts} className="text-xs text-red-400 hover:text-red-300 px-2 py-1">Chọn tất cả trùng</button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 bg-[#1e293b] border border-[#334155] rounded-lg p-3">
        <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)}
          className="bg-[#0f172a] border border-[#334155] rounded-md px-2 py-1.5 text-xs text-slate-200 min-w-[140px]" data-testid="filter-group">
          <option value="">Tất cả hội</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="bg-[#0f172a] border border-[#334155] rounded-md px-2 py-1.5 text-xs text-slate-200" data-testid="filter-status">
          <option value="">Tất cả TT</option>
          <option value="confirmed">Đã xác nhận</option>
          <option value="pending">Chờ duyệt</option>
          <option value="completed">Hoàn thành</option>
          <option value="cancelled">Đã huỷ</option>
        </select>
        <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
          className="bg-[#0f172a] border border-[#334155] rounded-md px-2 py-1.5 text-xs text-slate-200" data-testid="filter-from" />
        <span className="text-slate-500 text-xs">→</span>
        <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
          className="bg-[#0f172a] border border-[#334155] rounded-md px-2 py-1.5 text-xs text-slate-200" data-testid="filter-to" />
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          className="bg-[#0f172a] border border-[#334155] rounded-md px-2 py-1.5 text-xs text-slate-200" data-testid="sort-by">
          <option value="date_asc">Ngày tăng dần</option>
          <option value="date_desc">Ngày giảm dần</option>
          <option value="group">Theo hội</option>
          <option value="court">Theo sân</option>
        </select>
        <span className="text-xs text-slate-500 ml-auto">{displayed.length} lịch</span>
        <Button onClick={runOrphanCheck} size="sm" disabled={orphanLoading}
          className="bg-purple-500 hover:bg-purple-400 text-white font-semibold text-xs gap-1" data-testid="check-orphans-btn">
          {orphanLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
          Lịch lỗi
        </Button>
        <Button onClick={runConflictCheck} size="sm" disabled={conflictLoading}
          className="bg-amber-500 hover:bg-amber-400 text-[#0f172a] font-semibold text-xs gap-1" data-testid="check-conflicts-btn">
          {conflictLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
          Kiểm tra trùng
        </Button>
        {selected.size > 0 && (
          <Button onClick={() => setPinOpen(true)} size="sm" className="bg-red-500 hover:bg-red-600 text-white text-xs gap-1" data-testid="bulk-delete-btn">
            <Trash2 className="w-3 h-3" /> Xoá {selected.size} lịch
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#0f172a] z-10">
              <tr className="text-slate-400">
                <th className="px-2 py-2 text-left w-8">
                  <input type="checkbox" checked={selected.size === displayed.length && displayed.length > 0}
                    onChange={selectAll} className="rounded border-slate-500" data-testid="select-all" />
                </th>
                <th className="px-2 py-2 text-left">Hội</th>
                <th className="px-2 py-2 text-left">Sân</th>
                <th className="px-2 py-2 text-left">Ngày</th>
                <th className="px-2 py-2 text-left">Giờ</th>
                <th className="px-2 py-2 text-left">Trạng thái</th>
                <th className="px-2 py-2 text-right">Giá</th>
                <th className="px-2 py-2 text-center">Trùng</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">Đang tải...</td></tr>
              ) : displayed.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">Không có lịch hội</td></tr>
              ) : (
                displayed.map(s => (
                  <tr key={s.id} className={`border-t border-[#334155]/30 hover:bg-[#334155]/10 ${selected.has(s.id) ? "bg-red-500/5" : ""} ${conflicts.has(s.id) ? "bg-red-500/5" : ""}`}>
                    <td className="px-2 py-1.5">
                      <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleSelect(s.id)} className="rounded border-slate-500" />
                    </td>
                    <td className="px-2 py-1.5 text-slate-200 font-medium">{s.group_name}</td>
                    <td className="px-2 py-1.5 text-teal-400">{s.court_name}</td>
                    <td className="px-2 py-1.5 text-slate-300">{formatDateVN(s.date)}</td>
                    <td className="px-2 py-1.5 text-slate-300 font-mono">{s.start_time}-{s.end_time}</td>
                    <td className="px-2 py-1.5">
                      <Badge className={`${STATUS_CLS[s.status] || ""} text-[10px] border-0`}>{s.status}</Badge>
                    </td>
                    <td className="px-2 py-1.5 text-right text-slate-400">{(s.total_price || 0).toLocaleString("vi-VN")}đ</td>
                    <td className="px-2 py-1.5 text-center">
                      {conflicts.has(s.id) && <span className="text-red-400 font-bold text-[10px]">TRÙNG</span>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PIN Dialog */}
      <PinDialog open={pinOpen} onOpenChange={setPinOpen} onConfirm={handleBulkDelete}
        title={`Xoá ${selected.size} lịch hội`} description={`Nhập mã PIN để xác nhận xoá ${selected.size} lịch đặt sân của hội.`} />

      {/* Conflict Check Dialog */}
      <Dialog open={conflictDialogOpen} onOpenChange={setConflictDialogOpen}>
        <DialogContent className="bg-[#0f172a] border-[#334155] max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Kết quả kiểm tra trùng lịch
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Kiểm tra toàn diện: lịch hội vs lịch hội + lịch hội vs booking thường
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-3 pr-1">
            {conflictData && conflictData.total_conflicts === 0 ? (
              <div className="text-center py-8 bg-lime-500/5 border border-lime-500/20 rounded-xl">
                <div className="text-lime-400 text-sm font-semibold">Không phát hiện trùng lịch</div>
                <div className="text-slate-500 text-xs mt-1">Tất cả lịch hội không bị xung đột</div>
              </div>
            ) : conflictData ? (
              <>
                {/* Summary */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-red-400">{conflictData.total_conflicts}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Tổng cặp trùng</div>
                  </div>
                  <div className="bg-[#1e293b] border border-amber-500/20 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-amber-400">{conflictData.group_vs_group}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Hội vs Hội</div>
                  </div>
                  <div className="bg-[#1e293b] border border-purple-500/20 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-purple-400">{conflictData.group_vs_regular}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Hội vs Booking thường</div>
                  </div>
                </div>

                {/* Action bar */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={selectAllConflictGroupBookings}
                    className="border-red-500/30 text-red-400 text-xs gap-1" data-testid="select-all-conflicts">
                    Chọn tất cả lịch hội trùng
                  </Button>
                  {conflictSelected.size > 0 && (
                    <Button size="sm" onClick={() => setConflictPinOpen(true)}
                      className="bg-red-500 hover:bg-red-600 text-white text-xs gap-1" data-testid="delete-conflicts-btn">
                      <Trash2 className="w-3 h-3" /> Xoá {conflictSelected.size} lịch trùng
                    </Button>
                  )}
                  <span className="text-[10px] text-slate-500 ml-auto">{conflictSelected.size} đã chọn</span>
                </div>

                {/* Conflict pairs */}
                <div className="space-y-2">
                  {conflictData.conflicts.map((c, idx) => (
                    <div key={idx} className="bg-[#1e293b] border border-red-500/20 rounded-lg p-3" data-testid={`conflict-pair-${idx}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={`text-[9px] ${c.type === "group_vs_group" ? "bg-amber-500/20 text-amber-400" : "bg-purple-500/20 text-purple-400"} border-0`}>
                          {c.type === "group_vs_group" ? "Hội vs Hội" : "Hội vs Thường"}
                        </Badge>
                        <span className="text-[10px] text-slate-500">{c.booking_a.court_name} | {c.booking_a.date} | Trùng: {c.overlap}</span>
                      </div>
                      {/* Booking A */}
                      <div className="flex items-center gap-2 py-1.5 px-2 rounded bg-[#0f172a]/50 mb-1">
                        <input type="checkbox" checked={conflictSelected.has(c.booking_a.id)}
                          onChange={() => toggleConflictSelect(c.booking_a.id)}
                          className="rounded border-slate-500 shrink-0" disabled={c.booking_a.source !== "group_auto"} />
                        <span className="text-xs text-slate-200 flex-1">{c.booking_a.group_name}</span>
                        <span className="text-[10px] text-teal-400 font-mono">{c.booking_a.start_time}-{c.booking_a.end_time}</span>
                        <Badge className="text-[9px] bg-sky-500/20 text-sky-400 border-0">{c.booking_a.source === "group_auto" ? "Hội" : "Thường"}</Badge>
                      </div>
                      {/* Booking B */}
                      <div className="flex items-center gap-2 py-1.5 px-2 rounded bg-[#0f172a]/50">
                        <input type="checkbox" checked={conflictSelected.has(c.booking_b.id)}
                          onChange={() => toggleConflictSelect(c.booking_b.id)}
                          className="rounded border-slate-500 shrink-0" disabled={c.booking_b.source !== "group_auto"} />
                        <span className="text-xs text-slate-200 flex-1">{c.booking_b.group_name}</span>
                        <span className="text-[10px] text-teal-400 font-mono">{c.booking_b.start_time}-{c.booking_b.end_time}</span>
                        <Badge className="text-[9px] bg-sky-500/20 text-sky-400 border-0">{c.booking_b.source === "group_auto" ? "Hội" : "Thường"}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setConflictDialogOpen(false)} className="text-slate-400">Đóng</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conflict delete PIN Dialog */}
      <PinDialog open={conflictPinOpen} onOpenChange={setConflictPinOpen} onConfirm={deleteConflictBookings}
        title={`Xoá ${conflictSelected.size} lịch trùng`} description={`Nhập mã PIN để xác nhận xoá ${conflictSelected.size} lịch đặt sân bị trùng.`} />

      {/* Orphan Schedule Check Dialog */}
      <Dialog open={orphanDialogOpen} onOpenChange={setOrphanDialogOpen}>
        <DialogContent className="bg-[#0f172a] border-[#334155] max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-purple-400" />
              Kiểm tra lịch lỗi
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Phát hiện lịch hội cũ bị xóa, đổi tên, hoặc thiếu thông tin thời gian
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-3 pr-1">
            {orphanData && orphanData.total_issues === 0 ? (
              <div className="text-center py-8 bg-lime-500/5 border border-lime-500/20 rounded-xl">
                <div className="text-lime-400 text-sm font-semibold">Không phát hiện lịch lỗi</div>
                <div className="text-slate-500 text-xs mt-1">Tất cả lịch hội đều hợp lệ</div>
              </div>
            ) : orphanData ? (
              <>
                {/* Filter buttons */}
                <div className="grid grid-cols-5 gap-2">
                  <button onClick={() => setOrphanFilter("")}
                    className={`rounded-lg p-3 text-center border transition-all ${!orphanFilter ? "border-lime-500/40 bg-lime-500/10 ring-1 ring-lime-500/30" : "border-[#334155] bg-[#1e293b] hover:border-slate-500"}`}>
                    <div className="text-2xl font-bold text-[#F8FAFC]">{orphanData.total_issues}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Tất cả</div>
                  </button>
                  <button onClick={() => setOrphanFilter("negative_time")}
                    className={`rounded-lg p-3 text-center border transition-all ${orphanFilter === "negative_time" ? "border-red-500/40 bg-red-500/10 ring-1 ring-red-500/30" : "border-[#334155] bg-[#1e293b] hover:border-red-500/30"}`}>
                    <div className="text-2xl font-bold text-red-400">{orphanData.negative_time_count || 0}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Giờ âm/sai</div>
                  </button>
                  <button onClick={() => setOrphanFilter("missing_time")}
                    className={`rounded-lg p-3 text-center border transition-all ${orphanFilter === "missing_time" ? "border-purple-500/40 bg-purple-500/10 ring-1 ring-purple-500/30" : "border-[#334155] bg-[#1e293b] hover:border-purple-500/30"}`}>
                    <div className="text-2xl font-bold text-purple-400">{orphanData.missing_time_count}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Thiếu giờ</div>
                  </button>
                  <button onClick={() => setOrphanFilter("orphan")}
                    className={`rounded-lg p-3 text-center border transition-all ${orphanFilter === "orphan" ? "border-red-500/40 bg-red-500/10 ring-1 ring-red-500/30" : "border-[#334155] bg-[#1e293b] hover:border-red-500/30"}`}>
                    <div className="text-2xl font-bold text-red-400">{orphanData.orphan_count}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Hội đã xóa</div>
                  </button>
                  <button onClick={() => setOrphanFilter("renamed")}
                    className={`rounded-lg p-3 text-center border transition-all ${orphanFilter === "renamed" ? "border-amber-500/40 bg-amber-500/10 ring-1 ring-amber-500/30" : "border-[#334155] bg-[#1e293b] hover:border-amber-500/30"}`}>
                    <div className="text-2xl font-bold text-amber-400">{orphanData.renamed_count}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Hội đổi tên</div>
                  </button>
                </div>

                {/* Action bar */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={selectAllOrphans}
                    className="border-red-500/30 text-red-400 text-xs gap-1" data-testid="select-all-orphans">
                    Chọn tất cả ({orphanFilter ? orphanData.issues.filter(i => i.issue === orphanFilter).length : orphanData.total_issues})
                  </Button>
                  {orphanSelected.size > 0 && (
                    <Button size="sm" onClick={() => setOrphanPinOpen(true)}
                      className="bg-red-500 hover:bg-red-600 text-white text-xs gap-1" data-testid="delete-orphans-btn">
                      <Trash2 className="w-3 h-3" /> Xoá {orphanSelected.size} lịch lỗi
                    </Button>
                  )}
                  {orphanFilter && (
                    <span className="text-[10px] text-slate-500">
                      Đang lọc: <strong className="text-slate-300">{orphanFilter === "negative_time" ? "Giờ âm" : orphanFilter === "missing_time" ? "Thiếu giờ" : orphanFilter === "orphan" ? "Hội đã xóa" : "Đổi tên"}</strong>
                    </span>
                  )}
                  <span className="text-[10px] text-slate-500 ml-auto">{orphanSelected.size} đã chọn</span>
                </div>

                {/* Issue list — filtered */}
                <div className="space-y-1.5">
                  {(orphanFilter ? orphanData.issues.filter(i => i.issue === orphanFilter) : orphanData.issues).map((item, idx) => {
                    const issueCls = item.issue === "negative_time"
                      ? "border-red-500/30 bg-red-500/10"
                      : item.issue === "orphan"
                      ? "border-red-500/20 bg-red-500/5"
                      : item.issue === "renamed"
                      ? "border-amber-500/20 bg-amber-500/5"
                      : "border-purple-500/20 bg-purple-500/5";
                    const issueLabel = item.issue === "negative_time" ? "Giờ âm" : item.issue === "orphan" ? "Hội đã xóa" : item.issue === "renamed" ? "Đổi tên" : "Thiếu giờ";
                    const issueColor = item.issue === "negative_time" ? "text-red-400 bg-red-500/20" : item.issue === "orphan" ? "text-red-400 bg-red-500/20" : item.issue === "renamed" ? "text-amber-400 bg-amber-500/20" : "text-purple-400 bg-purple-500/20";
                    return (
                      <div key={idx} className={`${issueCls} border rounded-lg p-3 flex items-center gap-3`} data-testid={`orphan-item-${idx}`}>
                        <input type="checkbox" checked={orphanSelected.has(item.id)}
                          onChange={() => toggleOrphanSelect(item.id)}
                          className="rounded border-slate-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs text-slate-200 font-medium">{item.group_name || "(không tên)"}</span>
                            <Badge className={`${issueColor} text-[9px] border-0`}>{issueLabel}</Badge>
                          </div>
                          <div className="text-[10px] text-slate-400">{item.detail}</div>
                          <div className="flex gap-3 mt-1 text-[10px] text-slate-500">
                            <span>{item.court_name}</span>
                            <span>{item.date}</span>
                            <span className="font-mono">{item.start_time || "??"}-{item.end_time || "??"}</span>
                            <span>{item.status}</span>
                            {item.source && item.source !== "group_auto" && (
                              <Badge className="text-[8px] bg-sky-500/20 text-sky-400 border-0">{item.source}</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOrphanDialogOpen(false)} className="text-slate-400">Đóng</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Orphan delete PIN Dialog */}
      <PinDialog open={orphanPinOpen} onOpenChange={setOrphanPinOpen} onConfirm={deleteOrphanBookings}
        title={`Xoá ${orphanSelected.size} lịch lỗi`} description={`Nhập mã PIN để xác nhận xoá ${orphanSelected.size} lịch đặt sân có vấn đề.`} />
    </div>
  );
}
